import { useCallback, useContext, useMemo } from 'react';
import { DealRoom } from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import confirm from 'modules/confirm';
import { useTranslation } from 'react-i18next';
import { SUCCESS_MESSAGES } from 'request-responses';

export default function usePremiumFeatures(
  deal?: Partial<DealRoom>,
  onChange?: (hasBossModeUnlockedPremiumFeatures: boolean) => void,
  setIsLoading?: (isLoading: boolean) => void,
) {
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const getHasUnlockPremiumFeatures = useCallback(
    (deal: Partial<DealRoom>) =>
      deal?.isPaid ||
      deal?.wantToBePrivate ||
      deal?.hasUnlockPremiumFeatures ||
      deal?.hasBossModeUnlockedPremiumFeatures,
    [],
  );

  const hasUnlockPremiumFeatures = useMemo(
    () => getHasUnlockPremiumFeatures(deal),
    [deal, getHasUnlockPremiumFeatures],
  );

  const canLockPremiumFeatures = useMemo(
    () =>
      isSuperAdmin &&
      !deal?.isPaid &&
      !deal?.wantToBePrivate &&
      !deal?.hasUnlockPremiumFeatures &&
      deal?.hasBossModeUnlockedPremiumFeatures,
    [isSuperAdmin, deal],
  );

  const canUnlockPremiumFeatures = useMemo(
    () =>
      isSuperAdmin &&
      !deal?.isPaid &&
      !deal?.wantToBePrivate &&
      !deal?.hasUnlockPremiumFeatures &&
      !deal?.hasBossModeUnlockedPremiumFeatures,
    [isSuperAdmin, deal],
  );

  const updateHasPremiumFeatures = useCallback(
    async (hasBossModeUnlockedPremiumFeatures: boolean) => {
      if (!isSuperAdmin) return;

      const title = t(
        hasBossModeUnlockedPremiumFeatures
          ? 'youAreUnlockingTitle'
          : 'youAreLockingTitle',
        {
          dealName: deal?.name,
        },
      );

      const content = t(
        hasBossModeUnlockedPremiumFeatures
          ? 'youAreUnlockingDescription'
          : 'youAreLockingDescription',
      );

      const submitBtnText = t(
        hasBossModeUnlockedPremiumFeatures ? 'unlockFeatures' : 'lockFeatures',
      );

      if (
        !(await confirm({
          title,
          content,
          submitBtnText,
          type: 'delete',
        }))
      ) {
        return;
      }

      try {
        setIsLoading?.(true);

        await api.dealRoom.updateHasPremiumFeatures(
          deal?._id,
          hasBossModeUnlockedPremiumFeatures,
        );

        onChange?.(hasBossModeUnlockedPremiumFeatures);

        popSuccess(
          t(
            SUCCESS_MESSAGES[
              hasBossModeUnlockedPremiumFeatures
                ? 'UNLOCKED_PREMIUM_FEATURES'
                : 'LOCKED_PREMIUM_FEATURES'
            ],
          ),
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading?.(false);
      }
    },
    [deal, isSuperAdmin, onChange, popServerError, popSuccess, setIsLoading, t],
  );

  return {
    hasUnlockPremiumFeatures,
    canLockPremiumFeatures,
    canUnlockPremiumFeatures,
    updateHasPremiumFeatures,
    getHasUnlockPremiumFeatures,
  };
}
